import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { useTherapist } from "../../../../context/TherapistProvider";
import { useOpenBillingPortal } from "../../application/usePlusSubscription";
import PaymentIssueBanner from "./PaymentIssueBanner";

// One key per notice: hiding the expiring card must not hide a declined
// renewal that arrives later in the same visit.
const DISMISSED_KEYS = {
    pastDue: "alchely.pastDueNoticeDismissed",
    cardExpiring: "alchely.cardExpiringNoticeDismissed",
} as const;

type Notice = keyof typeof DISMISSED_KEYS;

const readDismissed = (notice: Notice): boolean => {
    try {
        return window.sessionStorage.getItem(DISMISSED_KEYS[notice]) === "1";
    } catch {
        return false;
    }
};

/**
 * The declined renewal, or a card that will have expired by the next one,
 * wherever the therapist is in the app.
 *
 * Their plan still holds while Stripe retries, which is exactly why it is
 * easy to miss until it stops. Hidden for the rest of the visit once
 * dismissed, and on the billing screen, which says the same thing itself.
 */
export default function PastDueNotice() {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { therapist } = useTherapist();
    const portal = useOpenBillingPortal();
    const notice: Notice | null = therapist?.subscription_past_due
        ? "pastDue"
        : therapist?.subscription_card_expiring
          ? "cardExpiring"
          : null;
    const [dismissed, setDismissed] = useState<Notice[]>(() =>
        (Object.keys(DISMISSED_KEYS) as Notice[]).filter(readDismissed)
    );

    if (notice === null || dismissed.includes(notice)) return null;
    if (pathname.startsWith("/subscription/billing")) return null;

    const dismiss = () => {
        setDismissed((current) => [...current, notice]);
        try {
            window.sessionStorage.setItem(DISMISSED_KEYS[notice], "1");
        } catch {
            // A browser that keeps nothing simply shows it again next visit.
        }
    };

    return (
        <div className="mb-6">
            <PaymentIssueBanner
                title={
                    notice === "cardExpiring"
                        ? t("subscription.billing.cardExpiring.title")
                        : undefined
                }
                message={t(
                    notice === "cardExpiring"
                        ? "subscription.billing.cardExpiring.body"
                        : "subscription.billing.dunning.body"
                )}
                onUpdateCard={() => portal.mutate(true)}
                isBusy={portal.isPending}
                onDismiss={dismiss}
            />
        </div>
    );
}
